import { useEffect, useState, useCallback } from "react";

import { Product } from "@transfr-inc/dashboard-components/constants";
import { NotificationType } from "@transfr-inc/dashboard-components";

import { filterClassroomLoginCodesByUserId } from "@services/account.utils";
import container from "@src/container";

const ERROR_MESSAGE = "Something went wrong. Please try again.";

export function useSimPass({ userId, classroomId, product = Product.TS, onError }) {
  const [deletingSimPass, setDeletingSimPass] = useState(false);
  const [generatingSimPass, setGeneratingSimPass] = useState(false);
  const [simPass, setSimPass] = useState();

  const { accountService } = container;

  useEffect(() => {
    if (!userId || !classroomId) {
      return;
    }

    let cancelled = false;

    accountService
      .fetchMostRecentCodes({
        user_id: userId,
        classroom_id: classroomId,
        product,
      })
      .then((codes) => {
        if (!cancelled) {
          const classroomLoginCode = filterClassroomLoginCodesByUserId(
            codes,
            userId
          );
          setSimPass(classroomLoginCode);
        }
      })
      .catch(() => {
        // Silently fail - sim pass may not exist yet
      });

    return () => {
      cancelled = true;
    };
  }, [userId, classroomId, product, accountService]);

  const handleError = useCallback(() => {
    if (onError) {
      onError({
        type: NotificationType.error,
        message: ERROR_MESSAGE,
      });
    }
  }, [onError]);

  const buildPayload = useCallback((basePayload) => {
    return product !== Product.TS
      ? { ...basePayload, product }
      : basePayload;
  }, [product]);

  const generateSimPass = useCallback(async () => {
    setGeneratingSimPass(true);
    try {
      const payload = buildPayload({
        userIds: [userId],
        classroomId,
      });

      const [result] = await accountService.createLoginCodes(payload);
      setSimPass(result);
    } catch {
      handleError();
    } finally {
      setGeneratingSimPass(false);
    }
  }, [userId, classroomId, product, accountService, buildPayload, handleError]);

  const deleteSimPass = useCallback(async () => {
    setDeletingSimPass(true);
    try {
      const payload = buildPayload({
        userId,
        classroomId,
      });

      await accountService.deleteCodes(payload);
      setSimPass();
    } catch {
      handleError();
    } finally {
      setDeletingSimPass(false);
    }
  }, [userId, classroomId, product, accountService, buildPayload, handleError]);

  return {
    deletingSimPass,
    generatingSimPass,
    simPass,
    generateSimPass,
    deleteSimPass,
  };
}
